import React, { useMemo, useState } from 'react';
import { Menu, Button, Badge, Group, Text, Stack, Divider, Modal, ActionIcon, Tooltip, Skeleton, useMantineTheme } from '@mantine/core';
import { IconChevronDown, IconPlus, IconTrash } from '@tabler/icons-react';

interface Dashboard {
  dashboard_profile_id: string;
  dashboard_name: string;
  dashboard_type: 'business' | 'marketing';
  settings?: {
    selected_campaign_ids?: string[];
  };
  is_default?: number;
  is_system?: number;
  last_viewed?: string;
}

interface DashboardSwitcherProps {
  dashboards: Dashboard[];
  activeDashboardId: string;
  onSelectDashboard: (dashboardId: string) => void;
  onCreateNew: () => void;
  onDeleteDashboard: (dashboardId: string) => Promise<void>;
  isLoading?: boolean;
}

export function DashboardSwitcher({
  dashboards,
  activeDashboardId,
  onSelectDashboard,
  onCreateNew,
  onDeleteDashboard,
  isLoading = false,
}: DashboardSwitcherProps) {
  const theme = useMantineTheme();
  const dk = theme.colorScheme === 'dark';
  const [deleteConfirm, setDeleteConfirm] = useState<{ dashboardId: string; dashboardName: string } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [menuOpened, setMenuOpened] = useState(false);

  // Sort: active first, then by last_viewed
  const sortedDashboards = useMemo(() => {
    return [...dashboards].sort((a, b) => {
      if (a.dashboard_profile_id === activeDashboardId) return -1;
      if (b.dashboard_profile_id === activeDashboardId) return 1;
      
      const aViewed = new Date(a.last_viewed || 0).getTime();
      const bViewed = new Date(b.last_viewed || 0).getTime();
      return bViewed - aViewed;
    });
  }, [dashboards, activeDashboardId]);

  const campaignCount = (dashboard: Dashboard) => {
    const count = dashboard.settings?.selected_campaign_ids?.length || 0;
    return count === 1 ? '1 campaign' : `${count} campaigns`;
  };

  const isActive = (dashboardId: string) => dashboardId === activeDashboardId;

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm) return;
    setIsDeleting(true);
    try {
      await onDeleteDashboard(deleteConfirm.dashboardId);
      setDeleteConfirm(null);
    } finally {
      setIsDeleting(false);
    }
  };

  // Initial-load skeleton: parent is still fetching the user's dashboards.
  // Showing the trigger as a skeleton avoids a flash of "Switch dashboard"
  // before the menu has anything meaningful to switch between.
  if (isLoading && dashboards.length === 0) {
    return (
      <Group spacing="xs" style={{ alignItems: 'center' }}>
        <Skeleton height={28} width={150} radius="sm" />
      </Group>
    );
  }

  return (
    <>
      <Group spacing="xs" style={{ alignItems: 'center' }}>
        <Menu position="bottom-start" withArrow shadow="md" withinPortal opened={menuOpened} onChange={setMenuOpened}>
          <Menu.Target>
            <Button
              variant="subtle"
              rightIcon={<IconChevronDown size={16} />}
              size="sm"
              styles={{
                root: {
                  padding: '4px 12px',
                  height: 'auto',
                  fontSize: 14,
                },
              }}
            >
              Switch dashboard
            </Button>
          </Menu.Target>

          <Menu.Dropdown style={{ minWidth: 320, background: dk ? '#25262B' : undefined, borderColor: dk ? '#373A40' : undefined }}>
            <Menu.Label style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.1em', color: dk ? '#666' : '#AAAAAA', fontWeight: 700 }}>
              MY DASHBOARDS
            </Menu.Label>

            {sortedDashboards.map((dashboard) => (
              <Menu.Item
                key={dashboard.dashboard_profile_id}
                onClick={() => !isLoading && onSelectDashboard(dashboard.dashboard_profile_id)}
                style={{
                  backgroundColor: isActive(dashboard.dashboard_profile_id) ? (dk ? '#2D2545' : '#F0ECFF') : 'transparent',
                  borderLeft: isActive(dashboard.dashboard_profile_id) ? '3px solid #7C3AED' : 'none',
                  paddingLeft: isActive(dashboard.dashboard_profile_id) ? 'calc(16px - 3px)' : 16,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Stack spacing={2} style={{ width: '100%' }}>
                  <Group spacing="xs">
                    {isActive(dashboard.dashboard_profile_id) && <Text size="lg">✓</Text>}
                    <Text size="sm" fw={isActive(dashboard.dashboard_profile_id) ? 600 : 400}>
                      {dashboard.dashboard_name}
                    </Text>
                    {dashboard.is_default === 1 && (
                      <Badge size="xs" variant="light" color="violet" style={{ fontSize: 9, padding: '0 6px', height: 16, lineHeight: '16px' }}>
                        Default
                      </Badge>
                    )}
                  </Group>
                  <Text size="xs" color="dimmed">
                    {dashboard.dashboard_type === 'business' ? 'Business Dashboard' : 'Marketing Dashboard'} · {campaignCount(dashboard)}
                  </Text>
                </Stack>

                {/* Delete Icon - Only for user-created dashboards */}
                {!dashboard.is_system && (
                  <Tooltip label="Delete dashboard" withArrow>
                    <ActionIcon
                      size="xs"
                      variant="subtle"
                      color="red"
                      onClick={(e) => {
                        e.stopPropagation();
                        setMenuOpened(false);
                        setDeleteConfirm({
                          dashboardId: dashboard.dashboard_profile_id,
                          dashboardName: dashboard.dashboard_name,
                        });
                      }}
                      style={{ marginLeft: 8 }}
                    >
                      <IconTrash size={14} />
                    </ActionIcon>
                  </Tooltip>
                )}
              </Menu.Item>
            ))}

            <Divider my="sm" />

            <Menu.Item
              icon={<IconPlus size={14} />}
              onClick={onCreateNew}
              style={{
                color: dk ? '#A78BFA' : '#7C3AED',
                fontWeight: 500,
                paddingLeft: 16,
              }}
            >
                Create New Dashboard
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Group>

      {/* Delete Confirmation Modal */}
      <Modal
        opened={deleteConfirm !== null}
        onClose={() => setDeleteConfirm(null)}
        title="Delete Dashboard"
        centered
        zIndex={1000}
        withinPortal
      >
        <Stack spacing="md">
          <Text>
            Are you sure you want to delete <strong>{deleteConfirm?.dashboardName}</strong>? This action cannot be undone.
          </Text>
          <Group spacing="sm" style={{ justifyContent: 'flex-end' }}>
            <Button variant="subtle" onClick={() => setDeleteConfirm(null)}>
              Cancel
            </Button>
            <Button
              color="red"
              loading={isDeleting}
              onClick={handleDeleteConfirm}
            >
              Delete Dashboard
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
